import type { PettyCashEntry } from '@dcc-erp/shared';

export interface CashBookRow extends PettyCashEntry {
  /** Running cash-on-hand AFTER this entry (descending list, newest first). */
  bal: number;
}

export interface FloatRollup {
  count: number;
  total: number;
  lastEntryDate: string | null;
}

export interface CategorySummaryRow {
  categoryCode: string;
  count: number;
  amount: number;
}

const iso = (d: PettyCashEntry['entryDate']): string => new Date(d).toISOString().slice(0, 10);

// An entry has actually MOVED cash (posted a JV + decremented the float) only at
// the POSTED / REPLENISHED terminals. PENDING_APPROVAL (over-threshold, deferred
// JV) and REJECTED (inert) rows have NOT touched cash, so they must never move
// the running balance nor count toward any spend total/KPI — but they are still
// returned/visible so their status badge + approval panel stay reachable.
export function isCashAffecting(en: Pick<PettyCashEntry, 'status'>): boolean {
  return en.status === 'POSTED' || en.status === 'REPLENISHED';
}

/** First/last calendar day of a 'YYYY-MM' month, as ISO 'YYYY-MM-DD' strings. */
export function monthRange(yyyymm: string): { dateFrom: string; dateTo: string } {
  const parts = yyyymm.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this month
  const mm = String(m).padStart(2, '0');
  return { dateFrom: `${y}-${mm}-01`, dateTo: `${y}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

/** Chronological ascending running balance from `opening`, returned newest-first. */
export function buildCashBook(entries: readonly PettyCashEntry[], opening: number): CashBookRow[] {
  const asc = [...entries].sort(
    (a, b) =>
      iso(a.entryDate).localeCompare(iso(b.entryDate)) || a.voucherNo.localeCompare(b.voucherNo),
  );
  let bal = opening;
  const rows: CashBookRow[] = asc.map((en) => {
    // Only POSTED / REPLENISHED rows have moved cash — others carry forward the
    // balance unchanged (but are still returned so they render in the table).
    //
    // FIX 6 — the CASH effect is the cash that actually LEFT the tin, NOT the
    // gross. The server credits CASH_PETTY = amount − whtAmount and decrements
    // float.currentBalance by that same net (WHT is withheld, not paid out). So
    // the running balance must subtract (amount − whtAmount); subtracting the
    // gross would understate cash-on-hand by Σ WHT and diverge from
    // float.currentBalance + the float-decrement / replenish math.
    if (isCashAffecting(en)) bal -= Number(en.amount) - Number(en.whtAmount ?? 0);
    return { ...en, bal };
  });
  return rows.reverse();
}

export function rollupByFloat(entries: readonly PettyCashEntry[]): Map<string, FloatRollup> {
  const m = new Map<string, FloatRollup>();
  for (const en of entries) {
    // Skip non-cash-affecting rows (PENDING_APPROVAL / REJECTED) so per-float
    // counts + totals match the authoritative float balance.
    if (!isCashAffecting(en)) continue;
    const cur = m.get(en.floatId) ?? { count: 0, total: 0, lastEntryDate: null };
    cur.count += 1;
    cur.total += Number(en.amount);
    const d = iso(en.entryDate);
    if (!cur.lastEntryDate || d > cur.lastEntryDate) cur.lastEntryDate = d;
    m.set(en.floatId, cur);
  }
  return m;
}

export function summarizeByCategory(entries: readonly PettyCashEntry[]): CategorySummaryRow[] {
  const m = new Map<string, CategorySummaryRow>();
  for (const en of entries) {
    // Only cash-affecting rows count toward category spend (excludes
    // PENDING_APPROVAL / REJECTED).
    if (!isCashAffecting(en)) continue;
    const cur = m.get(en.categoryCode) ?? { categoryCode: en.categoryCode, count: 0, amount: 0 };
    cur.count += 1;
    cur.amount += Number(en.amount);
    m.set(en.categoryCode, cur);
  }
  return [...m.values()].sort((a, b) => b.amount - a.amount);
}
